/**
 * useSpeechAnalytics Hook
 * Provides speech analytics functionality for any component
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for analyzing speech transcripts
 * 
 * @returns {Object} Speech analytics functions and state
 */
const useSpeechAnalytics = () => {
    const { api } = useAuth();
    const [analytics, setAnalytics] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Analyze a transcript and get coaching feedback
     * 
     * @param {string} transcript - The text to analyze
     * @param {number} durationSeconds - Optional: actual duration of the speech
     * @param {boolean} saveToHistory - Whether to save to history (default: false)
     * @param {string} context - Context for the speech (e.g., 'inside_sales', 'evolve')
     * @param {string} sessionId - Optional session ID for tracking
     * @returns {Promise<Object>} The analytics results
     */
    const analyzeTranscript = useCallback(async (transcript, durationSeconds = null, saveToHistory = false, context = 'general', sessionId = null) => {
        if (!transcript || !transcript.trim()) {
            setError('No transcript to analyze');
            return null;
        }

        setIsAnalyzing(true);
        setError(null);

        try {
            // Choose endpoint based on whether we want to save to history
            const endpoint = saveToHistory ? '/speech-analytics/save' : '/speech-analytics/analyze';
            const payload = {
                transcript,
                duration_seconds: durationSeconds,
                ...(saveToHistory && { context, session_id: sessionId })
            };

            const response = await api.post(endpoint, payload);

            if (response.data.success && response.data.analytics) {
                setAnalytics(response.data.analytics);
                return response.data.analytics;
            } else {
                setError(response.data.error || 'Failed to analyze speech');
                return null;
            }
        } catch (err) {
            console.error('Speech analysis error:', err);
            setError('Failed to analyze speech. Please try again.');
            return null;
        } finally {
            setIsAnalyzing(false);
        }
    }, [api]);

    /**
     * Save analytics to history (for already-analyzed transcripts)
     * 
     * @param {string} transcript - The transcript text
     * @param {number} durationSeconds - Duration of speech
     * @param {string} context - Context (e.g., 'inside_sales', 'evolve', 'rpat')
     * @param {string} sessionId - Optional session ID
     * @returns {Promise<Object>} The saved analytics
     */
    const saveAnalyticsToHistory = useCallback(async (transcript, durationSeconds = null, context = 'general', sessionId = null) => {
        if (!transcript || !transcript.trim()) {
            return null;
        }

        try {
            const response = await api.post('/speech-analytics/save', {
                transcript,
                duration_seconds: durationSeconds,
                context,
                session_id: sessionId
            });

            return response.data.success ? response.data.analytics : null;
        } catch (err) {
            console.error('Failed to save analytics to history:', err);
            return null;
        }
    }, [api]);

    /**
     * Clear current analytics
     */
    const clearAnalytics = useCallback(() => {
        setAnalytics(null);
        setError(null);
    }, []);

    /**
     * Get a quick summary of the analytics
     */
    const getSummary = useCallback(() => {
        if (!analytics) return null;

        return {
            score: Math.round(analytics.overall_score || 0),
            rating: analytics.overall_rating || 'unknown',
            pace: analytics.pace?.words_per_minute || 0,
            fillerCount: analytics.filler_words?.total_count || 0,
            confidence: analytics.confidence?.level || 'unknown',
            clarity: Math.round((analytics.clarity?.clarity_score || 0) * 100)
        };
    }, [analytics]);

    /**
     * Check if analytics indicate good performance
     */
    const isGoodPerformance = useCallback(() => {
        if (!analytics) return false;
        return analytics.overall_score >= 70;
    }, [analytics]);

    return {
        analytics,
        isAnalyzing,
        error,
        analyzeTranscript,
        saveAnalyticsToHistory,
        clearAnalytics,
        getSummary,
        isGoodPerformance
    };
};

export default useSpeechAnalytics;
